import React, { useState, useEffect } from 'react';

const InstagramGallery: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(0);
  const [instagramPosts, setInstagramPosts] = useState<string[]>([]);
  const postsPerPage = 10; // 2x5 grid = 10 posts per page

  useEffect(() => {
    // Load Instagram posts from the text file
    const loadInstagramPosts = async () => {
      try {
        const response = await fetch('/instagram_photos.txt');
        const text = await response.text();
        
        // Split by double newlines to separate each Instagram post
        const posts = text.split('\n\n').filter(post => post.trim().length > 0);
        setInstagramPosts(posts);
      } catch (error) {
        console.error('Error loading Instagram posts:', error);
      }
    };

    loadInstagramPosts();
  }, []);

  useEffect(() => {
    // Load Instagram embed script when component mounts or when page changes
    if (instagramPosts.length > 0) {
      const script = document.createElement('script');
      script.src = '//www.instagram.com/embed.js';
      script.async = true;
      document.body.appendChild(script);

      // Process embeds after script loads
      script.onload = () => {
        if (window.instgrm) {
          window.instgrm.Embeds.process();
        }
      };

      return () => {
        document.body.removeChild(script);
      };
    }
  }, [instagramPosts, currentPage]);

  const totalPages = Math.ceil(instagramPosts.length / postsPerPage);
  const startIndex = currentPage * postsPerPage;
  const displayedPosts = instagramPosts.slice(startIndex, startIndex + postsPerPage);

  const goToPrevious = () => {
    setCurrentPage(prev => (prev > 0 ? prev - 1 : totalPages - 1));
  };

  const goToNext = () => {
    setCurrentPage(prev => (prev < totalPages - 1 ? prev + 1 : 0));
  };

  return (
    <section id="gallery" className="py-12 sm:py-16 md:py-20 px-4 sm:px-6 bg-soft-cream">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-8 sm:mb-12 md:mb-16">
          <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-deep-teal mb-4 sm:mb-6">
            Hair and Makeup Gallery
          </h2>
          <p className="text-sm sm:text-base md:text-lg lg:text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
            See the beautiful moments and stunning looks I've created for my clients.
          </p>
        </div>

        {/* Carousel Container */}
        <div className="relative">
          {/* Navigation Buttons */}
          {totalPages > 1 && (
            <>
              <button
                onClick={goToPrevious}
                className="absolute left-0 top-1/2 transform -translate-y-1/2 z-10 bg-white bg-opacity-90 hover:bg-opacity-100 text-deep-teal p-3 rounded-full shadow-lg transition-all duration-300 hover:scale-110"
                aria-label="Previous images"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              
              <button
                onClick={goToNext}
                className="absolute right-0 top-1/2 transform -translate-y-1/2 z-10 bg-white bg-opacity-90 hover:bg-opacity-100 text-deep-teal p-3 rounded-full shadow-lg transition-all duration-300 hover:scale-110"
                aria-label="Next images"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </>
          )}

          {/* Instagram Grid - Seamless */}
          <div className="grid grid-cols-2 md:grid-cols-5 instagram-carousel-grid">
            {displayedPosts.map((post, index) => (
              <div 
                key={`${currentPage}-${index}`}
                className="instagram-post-container"
                dangerouslySetInnerHTML={{ __html: post }}
              />
            ))}
          </div>
        </div>

        {/* Page Indicators */}
        {totalPages > 1 && (
          <div className="flex justify-center mt-6 sm:mt-8 space-x-2">
            {Array.from({ length: totalPages }, (_, index) => (
              <button
                key={index}
                onClick={() => setCurrentPage(index)}
                className={`w-3 h-3 rounded-full transition-all duration-300 ${
                  index === currentPage 
                    ? 'bg-deep-teal scale-125' 
                    : 'bg-gray-300 hover:bg-gray-400'
                }`}
                aria-label={`Go to page ${index + 1}`}
              />
            ))}
          </div>
        )}

        {/* Page Counter */}
        {totalPages > 1 && (
          <div className="text-center mt-3 sm:mt-4">
            <p className="text-gray-600 text-xs sm:text-sm md:text-base">
              Page {currentPage + 1} of {totalPages} • {instagramPosts.length} total posts
            </p>
          </div>
        )}
      </div>

      <style dangerouslySetInnerHTML={{
        __html: `
          .instagram-carousel-grid {
            gap: 0 !important;
            border-radius: 12px;
            overflow: hidden;
            box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
          }
          
          .instagram-post-container {
            position: relative;
            overflow: hidden;
            aspect-ratio: 1;
            height: 280px;
          }

          /* Hide the Instagram iframe borders and make it fill container */
          .instagram-post-container iframe {
            width: 100% !important;
            height: 100% !important;
            border: none !important;
            border-radius: 0 !important;
            margin: 0 !important;
            padding: 0 !important;
            box-shadow: none !important;
            background: white !important;
          }

          /* Hide Instagram blockquote fallback */
          .instagram-post-container blockquote {
            display: none !important;
          }

          /* Crop the iframe to show only the image portion */
          .instagram-post-container {
            position: relative;
            overflow: hidden;
          }

          .instagram-post-container iframe {
            position: absolute;
            top: -60px !important;  /* Hide header */
            left: -40px !important;  /* Shift view to show more of the right side */
            width: 120% !important;  /* Wider to accommodate the shift */
            height: calc(100% + 120px) !important;  /* Extend to hide footer */
            transform: scale(1.0);  /* Reduced scale to make images smaller */
            transform-origin: center;
          }

          @media (max-width: 768px) {
            .instagram-carousel-grid {
              grid-template-columns: repeat(2, 1fr) !important;
            }
            
            .instagram-post-container {
              height: 220px;
            }

            .instagram-post-container iframe {
              top: -80px !important;
              left: -40px !important;  /* Shift view to the right on mobile too */
              width: 115% !important;  /* Wider to accommodate the shift */
              height: calc(100% + 80px) !important;
              transform: scale(1);  /* Smaller scale for mobile */
              transform-origin: center;
            }
          }

          @media (min-width: 769px) {
            .instagram-carousel-grid {
              grid-template-columns: repeat(5, 1fr) !important;
            }
          }
        `
      }} />
    </section>
  );
};

// Extend Window interface for Instagram embed
declare global {
  interface Window {
    instgrm: {
      Embeds: {
        process: () => void;
      };
    };
  }
}

export default InstagramGallery;
